/**
 * Sash IQ data layer for the dashboard (HS-958).
 *
 * The score card is fed by one GET — `/api/sash-iq/user/{userId}` answers the ring (score +
 * band) and the contributing factors from the same calculation run (HS-1233/HS-1234). It is
 * read here, through the generated fetcher, and mapped to the one view model the card renders,
 * so the card stays presentational and the DTO shape never leaks into JSX.
 *
 * Query options are built with `queryOptions()` rather than Orval's
 * `getGet…QueryOptions()`: the generated helper adds an `enabled` guard, which
 * `useSuspenseQuery` rejects. The query *keys* still come from the generated helpers, so a
 * cache entry written by a loader is the same one any generated hook would read.
 */
import {
  getApiSashIqUserUserId,
  getGetApiSashIqUserUserIdQueryKey,
  NormalizedApiError,
  type SashIQBand,
  type ScoreFactor,
  type UserSashIqScoreDto,
} from '@smartsash/api-client';
import type { BadgeColor, ProgressColor } from '@smartsash/ui';
import { queryOptions } from '@tanstack/react-query';

/**
 * Coarse rating band, per the product spec's four bands:
 * Excellent 80–100, Good 60–79, Developing 40–59, Needs Attention 0–39.
 */
export type ScoreRating = 'excellent' | 'good' | 'developing' | 'needsAttention';

/** Also the fallback for a value below every lower bound (i.e. a negative one). */
const LOWEST_RATING: ScoreRating = 'needsAttention';

/**
 * Highest band first — `find` takes the first bound the value clears.
 *
 * Exported because the Sash IQ page's chart draws one background lane per band (HS-958):
 * the lane heights are derived from these bounds, so the thresholds exist once.
 */
export const RATING_BANDS: { min: number; rating: ScoreRating }[] = [
  { min: 80, rating: 'excellent' },
  { min: 60, rating: 'good' },
  { min: 40, rating: 'developing' },
  { min: 0, rating: LOWEST_RATING },
];

export const MIN_SCORE = 0;
export const MAX_SCORE = 100;

/**
 * The colour each rating paints with. The colour *is* the reading, so a factor's bar
 * follows its own band, not the overall score's.
 *
 * Lives here rather than beside the card because the Sash IQ page colours a ring, a set of
 * chart marks and a legend from the same four bands (HS-958) — one table, four states.
 */
export const RATING_COLOR: Record<ScoreRating, ProgressColor> = {
  excellent: 'success',
  good: 'blue',
  developing: 'yellow',
  needsAttention: 'error',
};

/** The badge beside a rating states the same band the ring or bar is coloured by. */
export const RATING_BADGE_COLOR: Record<ScoreRating, BadgeColor> = {
  excellent: 'success',
  good: 'blue',
  developing: 'yellow',
  needsAttention: 'error',
};

/** The API's band enum → the rating it states. */
const BAND_RATINGS: Record<SashIQBand, ScoreRating> = {
  Excellent: 'excellent',
  Good: 'good',
  Developing: 'developing',
  NeedsAttention: LOWEST_RATING,
};

/** A factor's id (see `factorId`) → its translation key. */
const FACTOR_LABEL_KEYS = new Map<string, string>([
  ['compliance', 'dashboard.sashIq.factor.compliance'],
  ['position', 'dashboard.sashIq.factor.position'],
  ['hygiene', 'dashboard.sashIq.factor.hygiene'],
  ['consistency', 'dashboard.sashIq.factor.consistency'],
]);

/** A contributing factor, ready to render. */
export interface SashIqFactor {
  id: string;
  /** Raw API name — the label when the factor has no translation of its own. */
  name: string;
  /** Translation key, present only for the factors the UI knows. */
  labelKey?: string;
  /** 0–100. */
  value: number;
  rating: ScoreRating;
  /**
   * The same factor before the latest scored session (Sash IQ contract §4). Absent until the
   * backend serves it; `null` when there is no earlier reading. Either way the Sash IQ page
   * draws no direction rather than a guessed one.
   */
  previousScore?: number | null;
}

/** What the Sash IQ card renders. */
export interface SashIq {
  score: number;
  rating: ScoreRating;
  factors: SashIqFactor[];
  /**
   * The backend's verdict that the score rests on too little data to be shown (fewer than its
   * session/hour minimum in the window). The score still arrives alongside it; the screens
   * must not draw that number (HS-1471, Confluence 05 §11).
   */
  limitedData: boolean;
}

/**
 * The factor's stable `key`, case-folded and reduced to letters (HS-1234).
 *
 * Stage sends `"Compliance"`, HS-1233 proposed `"compliance"`; both are one factor. The result is
 * also a DOM id suffix and a translation-key segment, which is why it keeps letters only.
 */
function factorId(key: string): string {
  return key.toLowerCase().replace(/[^a-z]/g, '');
}

function clampScore(score: number): number {
  return Math.min(Math.max(Math.round(score), MIN_SCORE), MAX_SCORE);
}

/** The band a raw 0–100 value falls into. */
export function ratingForScore(score: number): ScoreRating {
  return RATING_BANDS.find((band) => score >= band.min)?.rating ?? LOWEST_RATING;
}

/**
 * The API's own `band` wins — it is what the backend formula decided — and the thresholds
 * are the fallback for a missing one, or for a value a newer backend added to the enum, so the
 * ring is never unlabelled.
 */
export function ratingFromBand(band: SashIQBand | null | undefined, score: number): ScoreRating {
  const rating = band == null ? undefined : (BAND_RATINGS[band] as ScoreRating | undefined);
  return rating ?? ratingForScore(score);
}

function toFactor(factor: ScoreFactor, index: number): SashIqFactor {
  const id = factor.key == null ? '' : factorId(factor.key);
  const labelKey = FACTOR_LABEL_KEYS.get(id);
  const value = clampScore(factor.score ?? MIN_SCORE);

  return {
    // A key can be missing; fall back to the position so the list key stays unique.
    id: id || `factor-${String(index)}`,
    // Translations follow `key`, never the display text, so renaming `name` keeps the label.
    name: factor.name ?? factor.key ?? '',
    ...(labelKey === undefined ? {} : { labelKey }),
    value,
    rating: ratingForScore(value),
  };
}

/**
 * The Sash IQ response as the card's view model, or `null` when the user has no score at all
 * (HS-1402) — see `notFoundToNull` for why the read can be absent.
 *
 * A score of `0` is a real reading and *not* an absence: the distinction is whether the
 * backend answered with a score object, not what number it holds.
 */
export function toSashIq(dto: UserSashIqScoreDto | null): SashIq | null {
  if (dto === null) {
    return null;
  }

  const value = clampScore(dto.score ?? MIN_SCORE);

  return {
    score: value,
    rating: ratingFromBand(dto.band, value),
    factors: (dto.factors ?? []).map(toFactor),
    // Read once, here: every screen takes it from the model rather than re-deriving it, which
    // is how the dashboard came to ignore it.
    limitedData: dto.limitedData ?? false,
  };
}

/** HTTP status the backend answers with for a user who has no score yet. */
const STATUS_NOT_FOUND = 404;

/**
 * A 404 from the Sash IQ read is the backend's normal answer for "this user has no score
 * yet" (`{"error":"User score not found","errorCode":"NotFound"}`), not a failure — so it
 * resolves to `null` and the screens render their pre-score state.
 *
 * Everything else still rejects and reaches the route's error boundary. Before HS-1402 the
 * 404 rejected too, which took down the whole `/dashboard` and `/sash-iq` routes for any user
 * who had not yet earned a score.
 *
 * The discrimination is on the HTTP status rather than on `errorCode`: the status is the
 * contract, the body's extension member is a detail this backend happens to send.
 */
export async function notFoundToNull<T>(read: Promise<T>): Promise<T | null> {
  try {
    return await read;
  } catch (error) {
    if (error instanceof NormalizedApiError && error.status === STATUS_NOT_FOUND) {
      return null;
    }
    throw error;
  }
}

/**
 * The one Sash IQ read (HS-1234): score, band, trend and factors from a single calculation run.
 * `/details` answered the same body and is no longer read.
 */
export const sashIqQuery = (userId: string) =>
  queryOptions({
    queryKey: getGetApiSashIqUserUserIdQueryKey(userId),
    // Four arguments since the route gained query params: (userId, params, options, signal).
    queryFn: ({ signal }) =>
      notFoundToNull(getApiSashIqUserUserId(userId, undefined, undefined, signal)),
  });
